import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient, useIsFetching } from '@tanstack/react-query';
import { on } from '@/lib/events';
import {
  POI_CONFIG,
  POI_BY_GROUP,
  POI_GROUPS_ORDER,
  POI_VISIBLE,
  GROUP_LABELS,
} from './poi-config';
import { usePoiStore } from './store';
import type { PoiCategory, PoiGroup } from './types';

function CategoryRow({ cat }: { cat: PoiCategory }) {
  const { t } = useTranslation();
  const cfg = POI_CONFIG[cat];
  const { active, toggle } = usePoiStore();
  const isOn = active.has(cat);
  const fetchingCount = useIsFetching({ queryKey: ['poi', cat] });
  const isLoading = isOn && fetchingCount > 0;
  const queryClient = useQueryClient();
  const cached = queryClient.getQueryData(['poi', cat]);
  const featureCount = cached
    ? (cached as { features: unknown[] }).features.length
    : null;

  return (
    <label className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-xs hover:bg-slate-50">
      <input
        type="checkbox"
        checked={isOn}
        onChange={() => toggle(cat)}
        className="cursor-pointer"
      />
      <span
        className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
        style={{ background: cfg.color }}
      />
      <span className="flex-1 truncate text-slate-700">
        <span className="mr-1">{cfg.emoji}</span>
        {t(`poi.category.${cat}`, { defaultValue: cfg.label })}
      </span>
      {isLoading && <span className="text-[10px] text-slate-400">…</span>}
      {!isLoading && featureCount !== null && (
        <span className="text-[10px] text-slate-400">
          {featureCount >= 1000
            ? `${(featureCount / 1000).toFixed(1)}k`
            : featureCount.toLocaleString('ru')}
        </span>
      )}
    </label>
  );
}

function GroupHeader({ group }: { group: PoiGroup }) {
  const { t } = useTranslation();
  const cats = POI_BY_GROUP[group];
  const { active, setMany } = usePoiStore();
  const allOn = cats.every((c) => active.has(c));
  const someOn = cats.some((c) => active.has(c)) && !allOn;

  const onToggle = () => {
    const others = Array.from(active).filter((c) => !cats.includes(c));
    setMany(allOn ? others : [...others, ...cats]);
  };

  const groupLabel = t(`poi.group.${group}`, { defaultValue: GROUP_LABELS[group] });

  return (
    <label className="flex cursor-pointer items-center gap-1.5 px-1.5 pb-0.5 pt-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500 hover:text-slate-700">
      <input
        type="checkbox"
        checked={allOn}
        ref={(el) => {
          if (el) el.indeterminate = someOn;
        }}
        onChange={onToggle}
        className="cursor-pointer"
        aria-label={t('poi.groupEnableAria', { group: groupLabel })}
      />
      {groupLabel}
    </label>
  );
}

export function PoiPanel() {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(true);
  const { active, setMany } = usePoiStore();

  // Esc закрывает раскрытую панель
  useEffect(() => {
    if (collapsed) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setCollapsed(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [collapsed]);

  // PoiAutoHint emits this when «Настроить» is clicked.
  useEffect(() => on('poi:openPanel', () => setCollapsed(false)), []);

  const allOn = useMemo(
    () => POI_VISIBLE.every((c) => active.has(c)),
    [active],
  );
  const someOn = active.size > 0 && !allOn;

  if (collapsed) {
    return (
      <button
        data-tour="poi"
        onClick={() => setCollapsed(false)}
        className="absolute right-4 top-28 z-[1000] inline-flex min-h-[40px] items-center rounded-lg bg-white/90 px-3 py-2 text-sm font-medium text-slate-700 shadow-md backdrop-blur-sm hover:bg-white sm:top-4"
        title={t('poi.open')}
        aria-label={t('poi.openAria')}
      >
        🎯 POI{active.size > 0 ? ` · ${active.size}` : ''}
      </button>
    );
  }

  return (
    <>
      {/* Mobile backdrop */}
      <div
        onClick={() => setCollapsed(true)}
        className="fixed inset-0 z-[1009] bg-black/30 sm:hidden"
        aria-hidden="true"
      />
      <aside
        data-tour="poi"
        className="absolute right-2 top-16 z-[1010] flex max-h-[80dvh] w-[calc(100vw-1rem)] flex-col rounded-lg bg-white/95 shadow-lg backdrop-blur-sm sm:right-4 sm:top-4 sm:z-[1000] sm:w-64"
      >
      <div className="flex items-center justify-between border-b border-slate-100 px-2 py-1.5">
        <span className="text-xs font-semibold text-slate-800">{t('poi.title')}</span>
        <button
          onClick={() => setCollapsed(true)}
          className="flex h-9 w-9 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          title={t('poi.close')}
          aria-label={t('poi.closeAria')}
        >
          ✕
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        <label className="flex cursor-pointer items-center gap-2 rounded border border-slate-200 px-1.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
          <input
            type="checkbox"
            checked={allOn}
            ref={(el) => {
              if (el) el.indeterminate = someOn;
            }}
            onChange={() => setMany(allOn ? [] : POI_VISIBLE)}
            className="cursor-pointer"
          />
          {t('poi.enableAll')}
        </label>

        {POI_GROUPS_ORDER.map((group) => {
          const cats = POI_BY_GROUP[group];
          if (!cats?.length) return null;
          return (
            <div key={group} className="mt-1">
              <GroupHeader group={group} />
              <div className="flex flex-col">
                {cats.map((cat) => (
                  <CategoryRow key={cat} cat={cat} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </aside>
    </>
  );
}
